/**
 * DylanEmbed — the DYLAN chat bubble (bottom-right) and its glass panel.
 *
 * DYLAN is the suite's AI IT assistant (https://dylan.ballyvesey.com, Azure
 * Container Apps). It is embedded as an iframe, exactly as OmniKnow does it:
 *   1. GET /api/dylan/status — may the bubble show? (DYLAN's own health +
 *      kill switch, cached server-side). Nothing renders until this says yes.
 *   2. On first open, GET /api/dylan/token — a one-hour HMAC embed token for
 *      the signed-in operator — and load `${url}?embedded=true&token=…`.
 *
 * Visible only with the `dylan-assistant` permission (admins implicitly).
 *
 * TOKEN BRIDGE (what Omni Cloud adds over the other hosts): DYLAN's live
 * lookups — AD detail, group members, server status, ServiceDesk — run through
 * THIS API as the operator, so DYLAN needs an Omni access token. This page
 * already has one (the same token every apiGet sends). When the iframe posts
 * `dylan:ready` or `dylan:need-omni-token`, we answer with
 * `{ type: 'dylan:omni-token', token }` — targeted at DYLAN's origin only —
 * and DYLAN forwards it as X-Omni-Token. No second sign-in inside the iframe,
 * and every relayed call is still attributed to the operator server-side.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/state/authStore.js';
import { acquireApiToken } from '@/auth/msal.js';
import { fetchDylanStatus, fetchDylanToken } from '@/api/endpoints/dylan.js';

export default function DylanEmbed() {
  const canUse = useAuthStore((s) => s.hasPermission('dylan-assistant'));
  const [status, setStatus] = useState(null); // { enabled, url } | null while loading
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState('');
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  const dylanOrigin = status?.url ? safeOrigin(status.url) : null;

  // Availability — once per mount, only for people who may use DYLAN.
  useEffect(() => {
    if (!canUse) return undefined;
    let cancelled = false;
    fetchDylanStatus()
      .then((s) => { if (!cancelled) setStatus(s); })
      .catch(() => { if (!cancelled) setStatus({ enabled: false, url: '' }); });
    return () => { cancelled = true; };
  }, [canUse]);

  const sendOmniToken = useCallback(async () => {
    const frame = iframeRef.current;
    if (!frame?.contentWindow || !dylanOrigin) return;
    const token = await acquireApiToken();
    if (token) frame.contentWindow.postMessage({ type: 'dylan:omni-token', token }, dylanOrigin);
  }, [dylanOrigin]);

  // Answer DYLAN's requests for an Omni token — from DYLAN's origin only.
  useEffect(() => {
    if (!dylanOrigin) return undefined;
    function onMessage(event) {
      if (event.origin !== dylanOrigin) return;
      const type = event.data?.type;
      if (type === 'dylan:ready' || type === 'dylan:need-omni-token') void sendOmniToken();
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [dylanOrigin, sendOmniToken]);

  async function toggle() {
    if (!open && !src) {
      try {
        const { token, url } = await fetchDylanToken();
        setSrc(`${url}?embedded=true&token=${encodeURIComponent(token)}`);
        setError(null);
      } catch (err) {
        setError(err?.message || 'Could not start DYLAN. Try again in a moment.');
      }
    }
    setOpen((v) => !v);
  }

  if (!canUse || !status?.enabled) return null;

  return (
    <>
      <button
        type="button"
        className="dylan-bubble"
        onClick={() => void toggle()}
        aria-label={open ? 'Close DYLAN' : 'Ask DYLAN'}
        aria-expanded={open}
        title="Ask DYLAN"
      >
        <i className={`fas ${open ? 'fa-times' : 'fa-robot'}`} aria-hidden="true" />
      </button>

      {open && (
        <div className="dylan-panel" role="dialog" aria-label="DYLAN AI assistant">
          <div className="dylan-panel__header">
            <div className="dylan-panel__title">
              <i className="fas fa-robot" aria-hidden="true" />
              <div>
                <strong>DYLAN</strong>
                <span>IT assistant</span>
              </div>
            </div>
            <div className="dylan-panel__actions">
              <button
                type="button"
                className="dylan-panel__btn"
                onClick={() => window.open(status.url, '_blank', 'noopener')}
                title="Open DYLAN in a new tab"
                aria-label="Open DYLAN in a new tab"
              >
                <i className="fas fa-external-link-alt" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="dylan-panel__btn"
                onClick={() => setOpen(false)}
                title="Close"
                aria-label="Close"
              >
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </div>
          </div>
          {error ? (
            <div className="dylan-panel__error">
              <i className="fas fa-exclamation-circle" aria-hidden="true" /> {error}
            </div>
          ) : (
            <iframe
              ref={iframeRef}
              src={src}
              className="dylan-panel__frame"
              title="DYLAN AI assistant"
              allow="clipboard-write"
              onLoad={() => void sendOmniToken()}
            />
          )}
        </div>
      )}
    </>
  );
}

function safeOrigin(url) {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}
